import React, { useEffect, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import {
  ReadEntityNode,
  ReadEntityNodeData,
} from "src/constants/NodeDataTypes";
import { useEntitySchemas } from "src/entities/queries";
import { LocateEntityCard } from "src/entityActionNodes/LocateEntityCard";
import { OutputLocationCard } from "src/entityActionNodes/OutputLocationCard";
import { SelectEntityCard } from "src/entityActionNodes/SelectEntityCard";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  node: ReadEntityNode;
} & NodeEditorBaseProps<ReadEntityNodeData>;

export type ReadEntityNodeForm = ReadEntityNodeData & {};

export const ReadEntityNodeEditor: React.FC<Props> = ({
  node,
  immutable,
  isReactive,
  displayedError,
  onUpdate,
}) => {
  const formMethods = useForm<ReadEntityNodeForm>({
    defaultValues: node.data,
    ...(isReactive && { values: node.data }),
  });

  const { getValues, setValue } = formMethods;

  const { workspace } = useWorkspaceContext();
  const { data: entitySchemasPage, isLoading: isEntitySchemasLoading } =
    useEntitySchemas({
      baseUrl: workspace.base_url!,
    });

  const entitySchemas = useMemo(() => {
    return entitySchemasPage?.entities ?? [];
  }, [entitySchemasPage]);

  useSubmitForm({
    onChange: (newValues) => onUpdate({ newData: newValues }),
    disabled: isReactive,
    previousValues: node.data,
    subscribe: formMethods.subscribe,
  });

  // Automatically set the first entity schema if no entity schema is selected
  useEffect(() => {
    const entitySchema = getValues("entity_schema");
    if (!entitySchema && entitySchemas.length > 0) {
      setValue("entity_schema", entitySchemas[0]._id);
    }
  }, [entitySchemas, getValues, setValue]);

  const fieldErrors: FieldErrors | undefined = useMemo(
    () =>
      displayedError?.field_errors
        ? (convertFieldErrorsBeToFe(displayedError.field_errors) ?? {})
        : {},
    [displayedError],
  );

  return (
    <FormProvider {...formMethods}>
      <div className="space-y-4">
        <SelectEntityCard
          entitySchemas={entitySchemas}
          immutable={immutable}
          isLoading={isEntitySchemasLoading}
          title="Select the Entity type to read"
        />
        <LocateEntityCard
          entitySchemas={entitySchemas}
          fieldErrors={fieldErrors}
          immutable={immutable}
          isLoading={isEntitySchemasLoading}
        />
        <OutputLocationCard fieldErrors={fieldErrors} immutable={immutable} />
      </div>
    </FormProvider>
  );
};
